import {
  Box,
  Text,
  Image,
  Container,
  Heading,
  Button,
  Icon,
  VStack,
  useColorModeValue,
  SimpleGrid,
  HStack,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  FormHelperText,
  FormErrorMessage,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Divider,
  Checkbox,
  AspectRatio,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaBuilding,
  FaMobileAlt,
  FaFacebookF,
  FaEnvelope,
  FaSms,
  FaCheck,
  FaPlay,
} from "react-icons/fa";
import { IconType } from "react-icons";
import { useState, useEffect } from 'react';
import { submitWaitlistData } from '../../../services/waitlistService';

interface FeatureProps {
  icon: IconType;
  title: string;
  text: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  referredBy: string;
  consentToContact: boolean;
}

interface FormErrors {
  name: boolean;
  email: boolean;
  phone: boolean;
  consentToContact: boolean;
}

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  platformName: string;
  platformType: 'mobile' | 'vendor';
  userEmail: string;
}

interface WaitlistFormProps {
  isOpen: boolean;
  onClose: () => void;
  formData: FormData;
  formErrors: FormErrors;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCheckboxChange: (name: string, checked: boolean) => void;
  handleSubmit: () => void;
  type: 'mobile' | 'vendor';
  isSubmitting: boolean;
}

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
  title: string;
}

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, videoId, title }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="bg.card">
        <ModalHeader color="text.primary">{title}</ModalHeader>
        <ModalCloseButton color="text.primary" />
        <ModalBody pb={6}>
          <AspectRatio ratio={16 / 9}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title={title}
              allowFullScreen
              style={{ borderRadius: '0.5rem' }}
            />
          </AspectRatio>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const FeatureCard: React.FC<FeatureProps> = ({ icon, title, text }) => {
  const borderColor = title.includes("InvyteOut") ? "blue.400" : "purple.500";
  
  return (
    <Box
      p={6}
      bg="rgba(255, 255, 255, 0.2)"
      rounded="lg"
      position="relative"
      zIndex={2}
      height="280px"
      display="flex"
      flexDirection="column"
      width="100%"
    >
      <VStack spacing={4} align="start" height="100%">
        <Icon as={icon} boxSize={8} color={borderColor} />
        <VStack align="start" spacing={2} flex="1">
          <Heading size="md" color="text.primary">
            {title}
          </Heading>
          <Text color="text.secondary">
            {text}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
};

const WaitlistFormModal: React.FC<WaitlistFormProps> = ({
  isOpen,
  onClose,
  formData,
  formErrors,
  handleChange,
  handleCheckboxChange,
  handleSubmit,
  type,
  isSubmitting
}) => {
  const isMobile = type === 'mobile';
  const colorScheme = isMobile ? 'blue' : 'purple';
  const title = isMobile ? 'InvyteOut' : 'Vynterface';
  const nameLabel = isMobile ? 'Name' : 'Business Name';
  const namePlaceholder = isMobile ? 'Your name' : 'Your business name';
  const phonePlaceholder = isMobile ? 'Your phone number' : 'Business phone number';
  const helperText = isMobile 
    ? "We'll notify you when InvyteOut launches" 
    : "We'll notify you when Vynterface launches";

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg="bg.card">
        <ModalHeader color="text.primary">Sign up for {title} Waitlist</ModalHeader>
        <ModalCloseButton color="text.primary" />
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={formErrors.name}>
              <FormLabel color="text.primary">{nameLabel}</FormLabel>
              <Input 
                placeholder={namePlaceholder}
                name="name"
                value={formData.name}
                onChange={handleChange}
                bg="bg.input"
                color="text.primary"
                borderColor="border.input"
                _hover={{ borderColor: "border.hover" }}
                _focus={{ borderColor: "primary.500", boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)" }}
                isDisabled={isSubmitting}
              />
              {formErrors.name && (
                <FormErrorMessage>{nameLabel} is required</FormErrorMessage>
              )}
            </FormControl>
            
            <FormControl isRequired isInvalid={formErrors.email}>
              <FormLabel color="text.primary">Email</FormLabel>
              <Input 
                placeholder={isMobile ? "your.email@example.com" : "business@example.com"}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                bg="bg.input"
                color="text.primary"
                borderColor="border.input"
                _hover={{ borderColor: "border.hover" }}
                _focus={{ borderColor: "primary.500", boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)" }}
                isDisabled={isSubmitting}
              />
              {formErrors.email && (
                <FormErrorMessage>Valid email is required</FormErrorMessage>
              )}
            </FormControl>
            
            <FormControl isRequired isInvalid={formErrors.phone}>
              <FormLabel color="text.primary">Phone</FormLabel>
              <Input 
                placeholder={phonePlaceholder}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                bg="bg.input"
                color="text.primary"
                borderColor="border.input"
                _hover={{ borderColor: "border.hover" }}
                _focus={{ borderColor: "primary.500", boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)" }}
                isDisabled={isSubmitting}
              />
              {formErrors.phone && (
                <FormErrorMessage>Phone number is required</FormErrorMessage>
              )}
              <FormHelperText color="text.secondary">
                {helperText}
              </FormHelperText>
            </FormControl>
            
            <FormControl>
              <FormLabel color="text.primary">Referred By (optional)</FormLabel>
              <Input 
                placeholder="How did you hear about us?"
                name="referredBy"
                value={formData.referredBy}
                onChange={handleChange}
                bg="bg.input"
                color="text.primary"
                borderColor="border.input"
                _hover={{ borderColor: "border.hover" }}
                _focus={{ borderColor: "primary.500", boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)" }}
                isDisabled={isSubmitting}
              />
              <FormHelperText color="text.secondary">
                {isMobile ? "Friend, social media, etc." : "Partner, event, social media, etc."}
              </FormHelperText>
            </FormControl>
            
            <FormControl isRequired isInvalid={formErrors.consentToContact}>
              <Checkbox
                name="consentToContact"
                isChecked={formData.consentToContact}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleCheckboxChange('consentToContact', e.target.checked);
                }}
                colorScheme={colorScheme}
                isDisabled={isSubmitting}
              >
                <Text fontSize="sm" color="text.primary">
                  I consent to receive updates about {title} via text message and/or email
                </Text>
              </Checkbox>
              <FormHelperText color="text.secondary" ml="6">
                Message and data rates may apply.
              </FormHelperText>
              {formErrors.consentToContact && (
                <FormErrorMessage>Consent is required to join the waitlist</FormErrorMessage>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            colorScheme={colorScheme} 
            mr={3} 
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Submitting"
            isDisabled={!formData.consentToContact || isSubmitting}
          >
            Join Waitlist
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Success modal with share options
const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  userName,
  platformName,
  platformType,
  userEmail
}) => {
  const colorScheme = platformType === 'mobile' ? 'blue' : 'purple';
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Generate a referral link with the user's name encoded
  useEffect(() => {
    if (isOpen) {
      const baseUrl = window.location.origin;
      const path = window.location.pathname;
      const encodedName = encodeURIComponent(userName);
      const encodedEmail = encodeURIComponent(userEmail);
      const referralParam = `referral=${encodedName}&email=${encodedEmail}&type=${platformType}`;
      setReferralLink(`${baseUrl}${path}?${referralParam}`);
    }
  }, [isOpen, userName, userEmail, platformType]);
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Join me on the ${platformName} waitlist!`);
    const body = encodeURIComponent(
      `Hey,\n\nI just signed up for the ${platformName} waitlist and thought you might be interested too!\n\n` +
      `${platformType === 'mobile' ? 
        'InvyteOut is a new social planning app that helps you discover events and connect with friends.' : 
        'Vynterface is a new platform for venue management that helps businesses attract more customers.'}\n\n` +
      `Join the waitlist here: ${referralLink}\n\n` +
      `${userName}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };
  
  const handleShareSMS = () => {
    const message = encodeURIComponent(
      `Hey! I just joined the ${platformName} waitlist. Check it out and join me: ${referralLink}`
    );
    window.open(`sms:?&body=${message}`);
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg="bg.card">
        <ModalHeader color="text.primary">
          <HStack>
            <Icon as={FaCheck} color={`${colorScheme}.500`} />
            <Text>You're on the waitlist!</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="text.primary" />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <Text color="text.primary">
              Thanks for signing up for the {platformName} waitlist, {userName.split(' ')[0]}! 
              We'll be in touch soon with updates.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Function to submit data to waitlist
const submitToWaitlist = async (
  data: FormData, 
  platformName: string
): Promise<boolean> => {
  try {
    // Use the waitlist service to submit the data
    const success = await submitWaitlistData(data, platformName);
    
    if (success) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    // No longer simulating success in development mode
    return false;
  }
};

export const WaitingList: React.FC = () => {
  // No need for Graph token with client credentials flow
  const bgGradient = useColorModeValue(
    "linear(to-r, blue.400, purple.500)",
    "linear(to-r, blue.600, purple.700)"
  );
  
  // Modal controls
  const { 
    isOpen: isMobileModalOpen,
    onOpen: onMobileModalOpen,
    onClose: mobileModalClose
  } = useDisclosure();
  
  const { 
    isOpen: isVendorModalOpen,
    onOpen: onVendorModalOpen,
    onClose: vendorModalClose
  } = useDisclosure();
  
  // Store the referral info at component level so it persists
  const [referralInfo, setReferralInfo] = useState<string>('');
  
  // Modified close handlers to preserve referredBy
  const onMobileModalClose = () => {
    setMobileForm({
      name: '',
      email: '',
      phone: '',
      referredBy: referralInfo, // Preserve the referral info
      consentToContact: false
    });
    setMobileFormErrors({
      name: false,
      email: false,
      phone: false,
      consentToContact: false
    });
    mobileModalClose();
  };
  
  const onVendorModalClose = () => {
    setVendorForm({
      name: '',
      email: '',
      phone: '',
      referredBy: referralInfo, // Preserve the referral info
      consentToContact: false
    });
    setVendorFormErrors({
      name: false,
      email: false,
      phone: false,
      consentToContact: false
    });
    vendorModalClose();
  };
  
  const toast = useToast();
  
  // Form states
  const [mobileForm, setMobileForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    referredBy: '',
    consentToContact: false
  });
  
  const [vendorForm, setVendorForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    referredBy: '',
    consentToContact: false
  });
  
  // Form validation states
  const [mobileFormErrors, setMobileFormErrors] = useState<FormErrors>({
    name: false,
    email: false,
    phone: false,
    consentToContact: false
  });
  
  const [vendorFormErrors, setVendorFormErrors] = useState<FormErrors>({
    name: false,
    email: false,
    phone: false,
    consentToContact: false
  });

  // Submission states
  const [isMobileSubmitting, setIsMobileSubmitting] = useState<boolean>(false);
  const [isVendorSubmitting, setIsVendorSubmitting] = useState<boolean>(false);
  
  // Success modal states
  const { 
    isOpen: isMobileSuccessOpen,
    onOpen: onMobileSuccessOpen,
    onClose: onMobileSuccessClose
  } = useDisclosure();
  
  const { 
    isOpen: isVendorSuccessOpen,
    onOpen: onVendorSuccessOpen,
    onClose: onVendorSuccessClose
  } = useDisclosure();
  
  // Store submitted user info for sharing
  const [submittedMobileUser, setSubmittedMobileUser] = useState({ name: '', email: '' });
  const [submittedVendorUser, setSubmittedVendorUser] = useState({ name: '', email: '' });
  
  // Video modal controls
  const {
    isOpen: isIOVideoOpen,
    onOpen: onIOVideoOpen,
    onClose: onIOVideoClose
  } = useDisclosure();

  const {
    isOpen: isVynVideoOpen,
    onOpen: onVynVideoOpen,
    onClose: onVynVideoClose
  } = useDisclosure();
  
  // Generic form change handler
  const createChangeHandler = (
    setForm: React.Dispatch<React.SetStateAction<FormData>>, 
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      
      setForm(prev => ({
        ...prev,
        [name]: value
      }));
      
      if (name === 'name' || name === 'email' || name === 'phone') {
        setErrors(prev => ({
          ...prev,
          [name]: false
        }));
      }
    };
  };
  
  // Generic checkbox change handler
  const createCheckboxChangeHandler = (
    setForm: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) => {
    return (name: string, checked: boolean) => {
      setForm(prev => ({
        ...prev,
        [name]: checked
      }));
      
      if (name === 'consentToContact') {
        setErrors(prev => ({
          ...prev,
          consentToContact: false
        }));
      }
    };
  };
  
  // Generic submit handler
  const createSubmitHandler = (
    formData: FormData, 
    setForm: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
    onClose: () => void,
    platformName: string,
    setSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    onSuccessOpen: () => void,
    setSubmittedUser: React.Dispatch<React.SetStateAction<{name: string, email: string}>>
  ) => {
    return async () => {
      // Validate form
      const errors = {
        name: !formData.name,
        email: !formData.email || !/\S+@\S+\.\S+/.test(formData.email),
        phone: !formData.phone,
        consentToContact: !formData.consentToContact
      };
      
      setErrors(errors);
      
      if (errors.name || errors.email || errors.phone || errors.consentToContact) {
        return; // Don't submit if there are errors
      }
      
      try {
        setSubmitting(true);
        const success = await submitToWaitlist(formData, platformName);
        
        if (success) {
          onClose();
          setForm({ name: '', email: '', phone: '', referredBy: '', consentToContact: false });
          setSubmittedUser({ name: formData.name, email: formData.email });
          onSuccessOpen();
        } else {
          toast({
            title: "Something went wrong",
            description: "We couldn't add you to the waitlist. Please try again later.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          title: "Submission error",
          description: "An unexpected error occurred. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSubmitting(false);
      }
    };
  };
  
  // Create handlers using the factory functions
  const handleMobileFormChange = createChangeHandler(setMobileForm, setMobileFormErrors);
  const handleVendorFormChange = createChangeHandler(setVendorForm, setVendorFormErrors);
  const handleMobileCheckboxChange = createCheckboxChangeHandler(setMobileForm, setMobileFormErrors);
  const handleVendorCheckboxChange = createCheckboxChangeHandler(setVendorForm, setVendorFormErrors);
  
  const handleMobileSubmit = createSubmitHandler(
    mobileForm, 
    setMobileForm, 
    setMobileFormErrors, 
    onMobileModalClose,
    "InvyteOut",
    setIsMobileSubmitting,
    onMobileSuccessOpen,
    setSubmittedMobileUser
  );
  
  const handleVendorSubmit = createSubmitHandler(
    vendorForm, 
    setVendorForm, 
    setVendorFormErrors, 
    onVendorModalClose,
    "Vynterface",
    setIsVendorSubmitting,
    onVendorSuccessOpen,
    setSubmittedVendorUser
  );

  const features = [
    { 
      title: "InvyteOut Mobile App", 
      text: "Discover events, connect with friends, and explore new venues. Perfect for planning your social life on the go.", 
      icon: FaMobileAlt 
    },
    { 
      title: "Vynterface for Vendors", 
      text: "Manage your venue, attract more customers, and grow your business with our powerful web platform.", 
      icon: FaBuilding 
    },
  ];

  useEffect(() => {
    // Check for referral parameter in URL
    const searchParams = new URLSearchParams(window.location.search);
    const referrer = searchParams.get('referral');
    
    if (referrer) {
      const referralString = `${referrer}`;
      // Store referral info at component level
      setReferralInfo(referralString);
      
      setMobileForm(prev => ({
        ...prev,
        referredBy: referralString
      }));
      
      setVendorForm(prev => ({
        ...prev,
        referredBy: referralString
      }));
    }
  }, []);

  return (
    <Box as="main" minH="100vh" bg="background.default" display="flex" flexDirection="column">
      {/* Hero Section */}
      <Box bgGradient={bgGradient} color="white" py={{ base: 10, md: 12 }} px={{ base: 2, md: 0 }}>
        <Container maxW="container.xl">
          <Heading
            as="h1"
            size={{ base: "xl", md: "2xl" }}
            textAlign="center"
            fontWeight="bold"
          >
            InvyteOut is Coming Soon
          </Heading>
        </Container>
      </Box>

      {/* Features Section with centered logo */}
      <Box flex="1" py={6} bg="background.default" position="relative">
        {/* Center Logo */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1}
          opacity={0.15}
          pointerEvents="none"
          display={{ base: 'none', md: 'block' }}
        >
          <Image
            src="/images/InviteoutlogoOnlyClose.png"
            alt="InvyteOut Logo"
            boxSize={{ base: "300px", sm: "400px", md: "600px" }}
            objectFit="contain"
          />
        </Box>

        {/* Mobile Logo */}
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1}
          opacity={0.15}
          pointerEvents="none"
          display={{ base: 'block', md: 'none' }}
          w="100%"
          h="100%"
        >
          <Image
            src="/images/InviteoutlogoOnlyClose.png"
            alt="InvyteOut Logo"
            w="100%"
            h="100%"
            objectFit="contain"
          />
        </Box>

        <Container maxW="container.xl">
          <VStack spacing={10}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 40 }} w="full" mt={{ base: 20, md: 32 }}>
              {/* Left Card - InvyteOut */}
              <VStack spacing={32} align="center" w="full" maxW={{ base: "100%", md: "500px" }}>
                <Button
                  size="lg"
                  colorScheme="blue"
                  rightIcon={<FaArrowRight />}
                  onClick={onMobileModalOpen}
                >
                  Join the InvyteOut Waitlist
                </Button>
                <Box position="relative">
                  <Image
                    src="https://img.youtube.com/vi/h5a69SvUAUg/maxresdefault.jpg"
                    alt="iO Explainer Video"
                    borderRadius="2xl"
                    shadow="2xl"
                    objectFit="cover"
                    w={{ base: "200px", md: "240px" }}
                    h={{ base: "200px", md: "240px" }}
                    mx="auto"
                    onClick={onIOVideoOpen}
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{
                      transform: "scale(1.05)",
                      filter: "brightness(1.1)",
                      shadow: "xl"
                    }}
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="rgba(0, 0, 0, 0.3)"
                    borderRadius="2xl"
                    cursor="pointer"
                    onClick={onIOVideoOpen}
                    transition="all 0.3s ease"
                    _hover={{
                      bg: "rgba(0, 0, 0, 0.4)",
                      transform: "scale(1.05)"
                    }}
                  >
                    <Icon as={FaPlay} boxSize={10} color="white" />
                  </Box>
                </Box>
                <FeatureCard
                  icon={FaMobileAlt}
                  title="Make Connections with InvyteOut"
                  text="Your personal social ecosystem for coordinating meet-ups, managing events, and discovering amazing businesses with exclusive rewards."
                />
              </VStack>

              {/* Right Card - Vynterface */}
              <VStack spacing={32} align="center" w="full" maxW={{ base: "100%", md: "500px" }}>
                <Button
                  size="lg"
                  colorScheme="purple"
                  rightIcon={<FaArrowRight />}
                  onClick={onVendorModalOpen}
                >
                  Join the Vynterface Waitlist
                </Button>
                <Box position="relative">
                  <Image
                    src="https://img.youtube.com/vi/UbeVbdI9J7c/maxresdefault.jpg"
                    alt="Vynterface Explainer Video"
                    borderRadius="2xl"
                    shadow="2xl"
                    objectFit="cover"
                    w={{ base: "200px", md: "240px" }}
                    h={{ base: "200px", md: "240px" }}
                    mx="auto"
                    onClick={onVynVideoOpen}
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{
                      transform: "scale(1.05)",
                      filter: "brightness(1.1)",
                      shadow: "xl"
                    }}
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="rgba(0, 0, 0, 0.3)"
                    borderRadius="2xl"
                    cursor="pointer"
                    onClick={onVynVideoOpen}
                    transition="all 0.3s ease"
                    _hover={{
                      bg: "rgba(0, 0, 0, 0.4)",
                      transform: "scale(1.05)"
                    }}
                  >
                    <Icon as={FaPlay} boxSize={10} color="white" />
                  </Box>
                </Box>
                <FeatureCard
                  icon={FaBuilding}
                  title="Transform Your Business"
                  text="An all-in-one platform simplified to help you grow your business, engage with your community and foster long-term customer loyalty."
                />
              </VStack>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Middle Banner */}
      <Box bgGradient={bgGradient} color="white" py={{ base: 8, md: 10 }} px={8}>
        <Container maxW="container.xl">
        </Container>
      </Box>

      {/* Bottom Banner Section */}
      <Box bgGradient={bgGradient} color="white" py={0} px={8} mt={-6}>
        <Container maxW="container.xl">
          <VStack spacing={1} py={0}>
            <Heading
              as="h2"
              size="lg"
              textAlign="center"
              fontWeight="medium"
              letterSpacing="wide"
            >
              Live Life, Support Local, InvyteOut
            </Heading>
            <VStack spacing={0.25} maxW="800px">
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                textAlign="center"
                lineHeight="tall"
              >
                Be among the first to experience our revolutionary platforms aimed at
                improving your social-life and business advertisement efforts. We thank you for
                your enthusiastic support. We promise not to disappoint! 
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontStyle="italic"
              >
                - Team iO
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Waitlist Form Modals */}
      <WaitlistFormModal
        isOpen={isMobileModalOpen}
        onClose={onMobileModalClose}
        formData={mobileForm}
        formErrors={mobileFormErrors}
        handleChange={handleMobileFormChange}
        handleCheckboxChange={handleMobileCheckboxChange}
        handleSubmit={handleMobileSubmit}
        type="mobile"
        isSubmitting={isMobileSubmitting}
      />
      
      <WaitlistFormModal
        isOpen={isVendorModalOpen}
        onClose={onVendorModalClose}
        formData={vendorForm}
        formErrors={vendorFormErrors}
        handleChange={handleVendorFormChange}
        handleCheckboxChange={handleVendorCheckboxChange}
        handleSubmit={handleVendorSubmit}
        type="vendor"
        isSubmitting={isVendorSubmitting}
      />
      
      {/* Success Modals with Share Options */}
      <SuccessModal
        isOpen={isMobileSuccessOpen}
        onClose={onMobileSuccessClose}
        userName={submittedMobileUser.name}
        userEmail={submittedMobileUser.email}
        platformName="InvyteOut"
        platformType="mobile"
      />
      
      <SuccessModal
        isOpen={isVendorSuccessOpen}
        onClose={onVendorSuccessClose}
        userName={submittedVendorUser.name}
        userEmail={submittedVendorUser.email}
        platformName="Vynterface"
        platformType="vendor"
      />

      {/* Video Modals */}
      <VideoModal
        isOpen={isIOVideoOpen}
        onClose={onIOVideoClose}
        videoId="h5a69SvUAUg"
        title="InvyteOut App Overview"
      />
      
      <VideoModal
        isOpen={isVynVideoOpen}
        onClose={onVynVideoClose}
        videoId="UbeVbdI9J7c"
        title="Vynterface Platform Overview"
      />
    </Box>
  );
};

export default WaitingList; 